import { useCallback, useState } from "react";

import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActionableInsight } from "app/pages/activation/types/api";

import { buildInsightCopyPlainText } from "../helpers/buildInsightCopyPlainText";
import * as SC from "../styles";
import { INSIGHTS_FEEDBACK_THUMBS_DOWN } from "../texts";

import { InsightNegativeFeedbackModal } from "./InsightNegativeFeedbackModal";

type InsightFeedbackThumbsDownButtonProps = {
    insightIndex: number;
    isActive: boolean;
    onActiveChange: (active: boolean) => void;
    onClearLike: () => void;
    dataAutomation: string;
    insight: ActionableInsight;
};

export const InsightFeedbackThumbsDownButton = ({
    insightIndex,
    isActive,
    onActiveChange,
    onClearLike,
    dataAutomation,
    insight,
}: InsightFeedbackThumbsDownButtonProps) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [comment, setComment] = useState("");
    const tracking = activationTracking.homePageInsights;

    const openModal = useCallback(() => {
        tracking.trackInsightFeedbackModalView();
        setModalOpen(true);
    }, [tracking]);

    const closeModalSilently = useCallback(() => {
        setModalOpen(false);
    }, []);

    const dismissFeedbackModal = useCallback(() => {
        tracking.trackInsightFeedbackModalDismiss();
        setModalOpen(false);
    }, [tracking]);

    const handleThumbClick = useCallback(() => {
        if (!isActive) {
            onActiveChange(true);
            onClearLike();
            setComment("");
            openModal();
            tracking.trackInsightThumbsDown(buildInsightCopyPlainText(insight));

            return;
        }

        onActiveChange(false);
        setComment("");
        closeModalSilently();
    }, [closeModalSilently, insight, isActive, onActiveChange, onClearLike, openModal, tracking]);

    const handleSubmitFeedback = useCallback(() => {
        const trimmed = comment.trim();

        tracking.trackInsightFeedbackModalSubmit(trimmed);
        setComment("");
        closeModalSilently();
    }, [closeModalSilently, comment, tracking]);

    return (
        <>
            <Tooltip
                content={translate(INSIGHTS_FEEDBACK_THUMBS_DOWN)}
                placement="bottom"
            >
                <SC.StyledThumbsDownIconButton
                    type="flat"
                    iconName={isActive ? "thumb-down" : "thumb-down-outline"}
                    iconSize="xs"
                    dataAutomation={dataAutomation}
                    $mirrorIcon={isActive}
                    onClick={handleThumbClick}
                />
            </Tooltip>

            <InsightNegativeFeedbackModal
                isOpen={isModalOpen}
                insightRowIndex={insightIndex}
                comment={comment}
                onCommentChange={setComment}
                onClose={dismissFeedbackModal}
                onSubmit={handleSubmitFeedback}
                isSubmitting={false}
            />
        </>
    );
};
